import type { TourDocument } from "@heroncendre/schema";
import reservees from "./scenesReservees.json";

/**
 * ============================================================================
 *  scenesReservees.ts — Des scènes qu'on ne montre que sur demande
 * ----------------------------------------------------------------------------
 *  Certaines scènes sont utiles à qui prépare un événement, pas à qui découvre
 *  le lieu : toilettes, accès PMR, salle de bain d'appoint. Elles restent dans
 *  le document — l'éditeur les montre, on les règle comme les autres —, mais la
 *  VISITE ne les connaît que si l'adresse porte le paramètre qui les ouvre :
 *
 *    `scenesReservees.json`   { "<paramètre>": ["<scène>", …] }
 *    `/?pratique`             ouvre les scènes rangées sous « pratique »
 *
 *  Sans le paramètre, la scène est RETIRÉE du document que reçoit le player, et
 *  avec elle les hotspots qui y mènent : elle n'existe nulle part — ni dans la
 *  navigation, ni par un hotspot, ni par `?scene=`. Retirer plutôt que masquer :
 *  rien ne peut y conduire par un chemin qu'on aurait oublié.
 * ==========================================================================*/

const parParametre = reservees as Record<string, readonly string[]>;

/** Les scènes à retirer, compte tenu des paramètres présents dans l'adresse. */
export function scenesFermees(params: URLSearchParams): Set<string> {
  const fermees = new Set<string>();
  for (const [parametre, scenes] of Object.entries(parParametre)) {
    if (!params.has(parametre)) for (const id of scenes) fermees.add(id);
  }
  // Une scène ouverte par un paramètre reste ouverte même si un autre la range aussi.
  for (const [parametre, scenes] of Object.entries(parParametre)) {
    if (params.has(parametre)) for (const id of scenes) fermees.delete(id);
  }
  return fermees;
}

/** Le document de la visite, sans les scènes fermées ni les hotspots qui y mènent. */
export function sansScenesFermees(tour: TourDocument, fermees: ReadonlySet<string>): TourDocument {
  if (fermees.size === 0 || !tour.scenes.some((scene) => fermees.has(scene.id))) return tour;
  return {
    ...tour,
    scenes: tour.scenes
      .filter((scene) => !fermees.has(scene.id))
      .map((scene) => ({
        ...scene,
        hotspots: scene.hotspots.filter((hotspot) => !("target" in hotspot && fermees.has(hotspot.target))),
      })),
  };
}
